import type { IOnSubmitParams } from '@apps/Application/Application.types'
import type { SubmitAction } from '@apps/Application/widgets/AppSettingsForm/appSettingsForm.types'
import { getInputRules } from '@apps/Application/widgets/AppSettingsForm/getInputRules'
import PermissionsCollapse from '@apps/Application/widgets/AppSettingsForm/PermissionsCollapse'
import SubmitButton from '@apps/Application/widgets/SubmitButton'
import SettingsField from '@common/forms/SettingsField'
import { useIframe, useTranslate } from '@common/hooks'
import { getAppSlug } from '@common/utils/uuid'
import { EmptyBlock } from '@common/widgets/EmptySections'
import Footer from '@layouts/Footer'
import type { FormEventHandler } from 'react'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import styles from './styles.module.scss'


interface IProps {
  application: IApplication,
  settings: IApplicationSettingKeyed[],
  serverErrorMessage: string,
  onSubmit: (params: IOnSubmitParams, confirmations: { slot: boolean, permissions: boolean }) => FormEventHandler<HTMLFormElement> | undefined,
  permissions: IPermissionKeyed[],
  confirmationSlot: number | null,
  preventWindowClose: boolean,
  submitAction: SubmitAction
}


export default function AppSettingsForm({
  application,
  settings,
  serverErrorMessage,
  onSubmit,
  permissions,
  preventWindowClose,
  submitAction,
}: IProps): JSX.Element {
  const { translate, interpolate } = useTranslate()
  const { formState: { errors, isSubmitting } } = useFormContext()

  const iframeParams = useMemo(() => {
    return {
      title: interpolate(translate('%s application'), [application.name]),
      url: `/apps/${getAppSlug(application.id, application.name) || ''}`,
    }
  }, [])

  useIframe(iframeParams)

  return (
    <form onSubmit={onSubmit({
      replaceSlot: false, submitAction, preventWindowClose,
    }, { slot: true, permissions: true })} data-test="app-form" className={styles.formWrapper}>
      {settings.map((setting, ind) => (
        <SettingsField
          type={setting.type}
          name={setting.key}
          rules={getInputRules(setting)}
          error={errors[setting.key]?.message as string}
          label={setting.title}
          description={setting.description}
          options={setting.enum}
          key={setting.key}
          tabIndex={200 + ind}
          data-test={`${setting.key}-field`}
        />
      ))}
      {settings.length === 0 && (
        <EmptyBlock
          text={translate('This app does not have any settings.')}
          dataTest="app-no-settings"
          className={styles.emptySettings}
        />
      )}
      <PermissionsCollapse permissions={permissions} />
      {serverErrorMessage && (
        <p className={styles.serverError}>
          {serverErrorMessage}
        </p>
      )}
      <Footer className={styles.footer}>
        <div className={styles.actionBtnContainer}>
          <SubmitButton
            settings={settings}
            application={application}
            permissions={permissions}
            className={styles.actionBtn}
            type="submit"
            isLoading={isSubmitting}
            data-test="confirm-btn"
            tabIndex={404}
          />
        </div>
      </Footer>
    </form>
  )
}
